<template>
    <el-dialog
      v-model="visible"
      width="600px"
      @close="$emit('close')"
    >
      <div v-if="!editMode">
        <h3>{{ notice.title }}</h3>
        <!-- eslint-disable-next-line vue/no-v-html -- DOMPurify로 새니타이징된 값만 바인딩됨 (utils/sanitizeHtml.js) -->
        <div v-html="sanitizeHtml(notice.content)" class="notice-content" />
        <p><strong>기간:</strong> 

            {{ formatDateKorean(notice.start_date) }} ~ {{ formatDateKorean(notice.end_date) }}

        </p>
  
        <!-- ✅ 공지사항 관리자만 수정/삭제 버튼 보이게 -->
        <div v-if="user.isNoticeManager" class="mt-4">
          <el-button type="primary" @click="editMode = true">수정</el-button>
          <el-button type="danger" @click="deleteNotice">삭제</el-button>
          <el-button @click="$emit('close')">닫기</el-button>
        </div>
      </div>
  
      <div v-else>
        <el-input v-model="form.title" placeholder="제목" />
        <QuillEditor v-model:content="form.content" contentType="html" />
        <el-date-picker
          v-model="form.dates"
          type="datetimerange"
          start-placeholder="시작"
          end-placeholder="종료"
        />
        <div class="mt-4">
          <el-button type="success" @click="updateNotice">저장</el-button>
          <el-button @click="editMode = false">취소</el-button>
        </div>
      </div>



          <!-- 뎃글 리스트 -->
        <el-divider content-position="left">댓글
            <el-button size="small" text type="primary" @click="showComments = !showComments">
                {{ showComments ? '닫기' : '댓글 보기' }}
            </el-button>        
        </el-divider>
        
        <div v-if="showComments">

            <div v-if="comments.length === 0">등록된 댓글이 없습니다.</div>
            <div v-else>
                <div v-for="c in comments" :key="c.id" class="comment-box">
                    <p><strong>{{ c.created_by_name  }}</strong> 
                        <small>{{ formatDate(c.created_at) }}</small>
                    </p>

                    <div v-if="editId === c.id">
                        <!-- ✅ 수정 중일 때 -->
                        <el-input
                            type="textarea"
                            v-model="editContent"
                            rows="3"
                        />
                        <el-button size="small" type="success" @click="saveEdit">저장</el-button>
                        <el-button size="small" @click="editId = null">취소</el-button>
                    </div>

                    <div v-else>
                        <!-- ✅ 일반 보기 상태 -->

                        <p>{{ c.content }}</p>
                                
                        <!-- ✅ 본인 댓글일 때만 수정/삭제 버튼 노출 -->
                        <div v-if="c.created_by === currentUserId" style="margin-top: 5px">
                            <el-button text size="small" @click="startEdit(c)">수정</el-button>
                            <el-button text size="small" type="danger" @click="deleteComment(c.id)">삭제</el-button>
                        </div>
                    </div>

                    <el-divider />
                </div>
            </div>

            <el-input
                type="textarea"
                v-model="newComment"
                placeholder="댓글을 입력하세요"
                rows="3"
            />
            <el-button type="primary" @click="submitComment" style="margin-top: 10px">등록</el-button>
        </div>
    </el-dialog>



</template>
  
<script setup>

import { ref, watch,onMounted } from 'vue'
import { ElMessageBox, ElMessage } from 'element-plus'
import axiosInstance from "@/api/axios";
import { QuillEditor } from '@vueup/vue-quill'
import { useUserStore } from '@/stores/user'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

  
const props = defineProps({ notice: { type: Object, default: () => ({}) } })
const emit = defineEmits(['close', 'updated'])
  
const comments = ref([])
const newComment = ref('')
const visible = ref(true)
const editMode = ref(false)
  
const form = ref({ title: '', content: '', dates: [] })

const currentUserId = ref(null)
const editId = ref(null)
const editContent = ref('')
const showComments = ref(false)

const fetchComments = async () => {
    const res = await axiosInstance.get(`/notice-comments/?notice=${props.notice.id}`)
    comments.value = res.data.results
}

const user = useUserStore()


watch(() => props.notice, (n) => {
  if ( n) {
    fetchComments()
    form.value.title = n.title
    form.value.content = n.content
    form.value.dates = [n.start_date, n.end_date]
  }
}, { immediate: true })
  
const updateNotice = async () => {
    try {
      await axiosInstance.put(`/notices/${props.notice.id}/`, {
        title: form.value.title,
        content: form.value.content,
        start_date: form.value.dates[0],
        end_date: form.value.dates[1]
      })
      ElMessage.success('수정되었습니다.')
      emit('updated')
      emit('close')
    } catch {
      ElMessage.error('수정 실패')
    }
  }

const startEdit = (comment) => {
  editId.value = comment.id
  editContent.value = comment.content
}

const deleteComment = async (id) => {
  await axiosInstance.delete(`/notice-comments/${id}/`)
  fetchComments()
}

const saveEdit = async () => {
    try {    
        await axiosInstance.put(`/notice-comments/${editId.value}/`, {
            content: editContent.value,
            notice: props.notice.id   // ✅ 필수
        })
        ElMessage.success("댓글이 수정되었습니다.")

        editId.value = null
        editContent.value = ''
        fetchComments()
    } catch (err) {
        ElMessage.error("댓글 수정 실패")
        console.error(err)
    }        
}


const submitComment = async () => {
  if (!newComment.value.trim()) return
  await axiosInstance.post(`/notice-comments/`, {
    notice: props.notice.id,
    content: newComment.value,
    parent: null  // 또는 대댓글용 ID


  })
  newComment.value = ''
  fetchComments()
}

const formatDate = (d) => new Date(d).toLocaleString()

    
const deleteNotice = async () => {
    try {
      await ElMessageBox.confirm('이 공지를 삭제할까요?', '확인', { type: 'warning' })
      await axiosInstance.delete(`/notices/${props.notice.id}/`)
      ElMessage.success('삭제되었습니다.')
      emit('updated')
      emit('close')
    } catch  (err){
      console.warn('삭제 취소됨 또는 실패:', err)
  
  
    }
}

watch(showComments, async (visible) => {
  if (visible && comments.value.length === 0) {
    await fetchComments()
  }
})

onMounted(async () => {
  const res = await axiosInstance.get('/user/me/')
  currentUserId.value = res.data.id
})


const formatDateKorean = (isoString) => {
  const date = new Date(isoString)
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`
}

</script>
<!--
  ⚠ 전역(non-scoped) 유지 필요: .notice-content img 는 v-html 로 주입된 콘텐츠 대상이라
  scoped 로는 매칭되지 않음. 단, 셀렉터는 반드시 .notice-content 하위로만 작성할 것.
  (.mt-4 전역 중복 정의는 common.css 공식 유틸로 이전 — 2026-06-11)
-->
<style>
/* ✅ 공지 HTML 내 이미지 대응 */
.notice-content img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 10px auto;
}
</style>